import React, { useCallback, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import { components } from 'react-select'
import { getIn, setIn } from 'formik'
import { connectedApiService } from '../index'
import _ from 'lodash'
import { DropdownIndicator, MultiValueContainer } from './FormSelectComponents'

const { Control, Group, Label } = Form

export default function FormSelectAsync({
  field,
  form: {
    touched = {},
    errors = {},
    setFieldValue = () => {},
    setTouched = () => {},
    values = {},
  } = {},
  icon,
  groupProps = {},
  controlId,
  handleChange,
  isMulti = false,
  showSelectAll = false,
  isSearchable = false,
  disabled = false,
  endpoint = '',
  searchableParamKey = '',
  formatItems,
  limit = 30,
  defaultOptions,
  customFilter,
  customFilterChange,
  editPartnerCar = false,
  isRateCard = false,
  customLoadOptions = false,
  loadOptions,
  cacheOptions = true,
  closeMenuOnSelect = true,
  blurInputOnSelect = true,
  showMultiSelectValuesChips = false,
  classNamePrefix = 'react-select',
  placeholder = 'Select...',
  ...props
}) {
  const [searchableParamVal, setSearchableParamVal] = useState('')
  const [options, setOptions] = useState([])
  const [isFocused, setIsFocused] = useState(false)

  // Fetch options from API
  const getItems = useCallback(async () => {
    if (endpoint && !isRateCard) {
      const params = searchableParamKey
        ? `&filter[${searchableParamKey},like]=${searchableParamVal}`
        : ''
      var { data = [] } =
        (await connectedApiService.get(
          `${endpoint}?page=1&limit=${limit}${params}`
        )) || {}

      if (editPartnerCar) {
        data = data.filter(el => {
          return el !== null
        })
      }
      if (customFilter) {
        data = customFilter(data)
      }
      const items = formatItems ? data.map(formatItems) : data
      if (!data.length && defaultOptions.length) {
        const defaultItems = formatItems
          ? defaultOptions.map(formatItems)
          : defaultOptions
        setOptions(defaultItems)
        return defaultItems
      }

      const uniqueValues = _.uniqBy(items, 'value')
      setOptions(uniqueValues)
      return uniqueValues
    } else if (isRateCard) {
      const response = await connectedApiService.get(endpoint)
      const uniqueValues = _.uniqBy(response.data, 'id')
      setOptions(uniqueValues)
      return uniqueValues
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpoint,
    searchableParamVal,
    searchableParamKey,
    formatItems,
    limit,
    isRateCard,
  ])

  // Match field.value with options and set the field value
  useEffect(() => {
    if (field.name !== 'keyOwner') return
    if (field.value && options.length) {
      if (isMulti) {
        // For multi-select, field.value is an array of strings
        const matchedOptions = options.filter(option =>
          field.value.includes(option.value)
        )
        if (matchedOptions.length && !_.isEqual(field.value, matchedOptions)) {
          setFieldValue(field.name, matchedOptions)
        }
      } else {
        // For single-select, field.value is a string
        const matchedOption = options.find(
          option => option.value === field.value
        )
        if (matchedOption && field.value !== matchedOption) {
          setFieldValue(field.name, matchedOption)
        }
      }
    }
  }, [options, field.value, field.name, setFieldValue, isMulti])

  const showError = !!getIn(touched, field.name) && !!getIn(errors, field.name)

  const onChange = value => {
    let newValue
    if (value === null) {
      newValue = isMulti ? [] : { label: '', value: '' }
    } else {
      newValue = value
    }
    setFieldValue(field.name, newValue)
    if (handleChange) {
      handleChange(newValue)
    }
  }

  const onInputChange = val => {
    setSearchableParamVal(val)
  }

  const onSelectAll = e => {
    e.preventDefault()
    e.stopPropagation()
    const newValue =
      !field.value || field.value.length < options.length ? [...options] : []
    setFieldValue(field.name, newValue)
  }

  const onBlur = () => {
    const _touched = setIn({ ...touched }, field.name, true)
    setTouched(_touched)
    setIsFocused(false)
  }
  const onFocus = () => {
    setIsFocused(true)
  }
  const ValueContainer = isMulti
    ? MultiValueContainer
    : components.ValueContainer

  const Option = props => {
    if (props.data.type && props.data.type === 'globalRateVehicle') {
      return (
        <div className='selectOptionButton'>
          <components.Option {...props}>
            <div style={{ display: 'inlineBlock' }}>
              <span>{props.data.label}</span>
              <div style={{ float: 'right' }}>
                <span className='pr-1 vehicle-passenger-select-Async'>
                  {props.data.passenger}
                </span>
                <span className='pr-1 vehicle-luggage-select-Async'>
                  {props.data.luggage}
                </span>
              </div>
            </div>
          </components.Option>
        </div>
      )
    } else if (props.data.type === 'checkBox') {
      return (
        <div className='selectOptionCheckbox'>
          <components.Option {...props}>
            <div>
              <span>{props.data.label}</span>
            </div>
          </components.Option>
        </div>
      )
    } else if (
      props.data.label &&
      props.data.value &&
      (props.data.Passenger !== undefined ||
        props.data.passenger !== undefined ||
        props.data.luggage !== undefined)
    ) {
      return (
        <components.Option {...props}>
          <div style={{ display: '' }}>
            <span>{props.data.label}</span>
            <div style={{ float: 'right' }}>
              <span className='pr-1 vehicle-passenger-select-Async'>
                {props.data.Passenger !== undefined
                  ? props.data.Passenger
                  : props.data.passenger}
              </span>
              <span className='pr-1 vehicle-luggage-select-Async'>
                {props.data.luggage}
              </span>
            </div>
          </div>
        </components.Option>
      )
    } else {
      return <components.Option {...props} />
    }
  }

  return (
    <Group controlId={controlId || field.name}>
      <Label className='form-label'>{props.label}</Label>
      <AsyncSelect
        components={{ DropdownIndicator, ValueContainer, Option }}
        className={`react-select-container ${showError ? 'is-invalid' : ''}`}
        classNamePrefix={classNamePrefix}
        {...field}
        cacheOptions={cacheOptions}
        // https://react-select.com/async#defaultoptions from docs this prop has 2 valid values
        defaultOptions={
          customLoadOptions && !closeMenuOnSelect ? defaultOptions : true
        }
        inputId={controlId || field.name}
        closeMenuOnSelect={closeMenuOnSelect}
        blurInputOnSelect={blurInputOnSelect}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        onInputChange={onInputChange}
        isFocused={isFocused}
        // loadOptions={isRateCard ? cardOptions : getItems}
        loadOptions={inputValue =>
          customLoadOptions
            ? loadOptions(inputValue, values, setOptions)
            : getItems()
        }
        isSearchable={isSearchable}
        placeholder={placeholder}
        isDisabled={disabled}
        isAsync={true}
        isClearable={field.value && field.value.value}
        key={`${String(endpoint)}${customFilterChange}`}
        isMulti={isMulti}
        hideSelectedOptions={!isMulti}
        onSelectAll={showSelectAll ? onSelectAll : null}
        selectedAll={
          !!showSelectAll &&
          !!isMulti &&
          !!field.value &&
          field.value.length === options.length
        }
        options={options}
        getOptionLabel={option => {
          return option.label || ''
        }}
      />
      {showError && (
        <Control.Feedback className='d-block' type='invalid'>
          {getIn(errors, field.name)}
        </Control.Feedback>
      )}
    </Group>
  )
}
